import React, { useEffect, useState } from "react";
import { getAddedDocs, uploadPendingAddDoc, getAddDocDetailByID } from "../../services/api/AddDocApi";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { FiX, FiDownload, FiFileText, FiTrash2 } from "react-icons/fi";
import { BiUpload } from "react-icons/bi";
import { uploadFileWithProgress } from "../../utils/ftpUploadUtils";
import "./ViewDocumentModal.scss";

export const ViewDocumentModal = ({ isOpen, onClose, courtRef, mode = "view", documentData = null, onSuccess }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const { company } = useAuth();
  
  // Upload mode states
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const MAX_FILE_SIZE = 50 * 1024 * 1024;

  const getAcceptString = () => {
    return '.pdf,.doc,.docx,.txt,.png,.jpeg,.jpg,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/png,image/jpeg,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  };

  const validateFileType = (file) => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'image/png',
      'image/jpeg',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.png', '.jpg', '.jpeg', '.xls', '.xlsx'];

    if (!file) {
      return { isValid: false, error: 'No file provided' };
    }

    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      return { isValid: false, error: 'Invalid file type. Allowed formats: PDF, DOC, DOCX, TXT, PNG, JPEG, JPG, XLS, XLSX' };
    }

    if (file.size > MAX_FILE_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return { isValid: false, error: `File size (${sizeMB}MB) exceeds the maximum limit of 50MB` };
    }

    return { isValid: true };
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return "Unknown";
    const parts = url.split("/");
    return parts[parts.length - 1];
  };

  const getFileExtension = (url) => {
    if (!url) return "";
    const fileName = getFileNameFromUrl(url);
    const parts = fileName.split(".");
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "FILE";
  };

  const fetchDocuments = React.useCallback(async () => {
    setLoading(true);
    try {
      let uploadedDocs = [];
      
      if (documentData?._id) {
        // Per-card view: Fetch specific document by ID
        const response = await getAddDocDetailByID(documentData._id);
        
        // Check if document has uploaded files
        if (response.data && response.data.url && response.data.url.length > 0) {
          uploadedDocs = [response.data];
        }
      } else if (courtRef) {
        // Centralized view: Fetch all documents by court reference
        const response = await getAddedDocs(courtRef);
        uploadedDocs = response.data.filter(
          (doc) => doc.status === "Uploaded" && doc.url && doc.url.length > 0
        );
      }
      
      setDocuments(uploadedDocs);
    } catch (error) {
      console.error("Error fetching documents:", error);
      showToast("Failed to fetch documents", "error");
    } finally {
      setLoading(false);
    }
  }, [courtRef, documentData, showToast]);

  useEffect(() => {
  
  if (isOpen && mode === "view") {
    fetchDocuments();
  }
  
  // Initialize uploaded files for upload/edit mode
  if (isOpen && mode === "upload") {
    
    // Reset states first
    setUploadedFiles([]);
    setUploadingFiles([]);
    setUploadProgress({});
    
    // If editing existing document with URLs, show them
    if (documentData && documentData.url && Array.isArray(documentData.url) && documentData.url.length > 0) {
      const files = documentData.url.map((url, index) => ({
        id: `existing-${index}`,
        url: url,
        name: getFileNameFromUrl(url),
        isExisting: true
      }));
      setUploadedFiles(files);
    }
  }
  
  // Reset on close
  if (!isOpen) {
    setUploadedFiles([]);
    setUploadingFiles([]);
    setUploadProgress({});
  }
}, [isOpen, mode, documentData?._id]);

 const handleFileSelect = async (e) => {
  const files = Array.from(e.target.files);
  
  for (const file of files) {
    const validation = validateFileType(file);
    
    if (!validation.isValid) {
      showToast(validation.error, "error");
      continue;
    }

    const fileId = `upload-${Date.now()}-${Math.random()}`;
    
    setUploadingFiles(prev => {
      return [...prev, fileId];
    });
    setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

    try {
      const filePath = company._id ? `/${company._id}/others` : `/unknown`;

      await uploadFileWithProgress(
        file,
        filePath,
        // Progress callback
        (progress) => {
          setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
        },
        // Success callback
        (result) => {
          const uploadedUrl = result.imageUrl || result.success || result.url;
          
          if (!uploadedUrl) {
            console.error("No URL found in result:", result);
            showToast(`Failed to get URL for ${file.name}`, "error");
            setUploadingFiles(prev => prev.filter(id => id !== fileId));
            return;
          }
          
          setUploadedFiles(prev => {
            const newFile = {
              id: fileId,
              url: uploadedUrl,
              name: file.name,
              isExisting: false
            };
            const newFiles = [...prev, newFile];
            return newFiles;
          });
          
          setUploadingFiles(prev => {
            const filtered = prev.filter(id => id !== fileId);
            return filtered;
          });
          
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileId];
            return newProgress;
          });
          
          showToast(`${file.name} uploaded successfully!`, "success");
        },
        // Error callback
        (error) => {
          console.error("File upload failed:", error);
          setUploadingFiles(prev => prev.filter(id => id !== fileId));
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileId];
            return newProgress;
          });
          showToast(error.message || `Failed to upload ${file.name}`, "error");
        }
      );
    } catch (error) {
      console.error("File upload failed:", error);
      setUploadingFiles(prev => prev.filter(id => id !== fileId));
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[fileId];
        return newProgress;
      });
      showToast(error.message || `Failed to upload ${file.name}`, "error");
    }
  }
  
  // Clear file input
  e.target.value = '';
};

const handleRemoveFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

const handleSubmitDocuments = async () => {
  
  if (uploadedFiles.length === 0) {
    showToast("Please upload at least one document", "error");
    return;
  }

  if (!documentData?._id) {
    showToast("Document ID is missing", "error");
    return;
  }

  // Check if any files are still uploading
  if (uploadingFiles.length > 0) {
    showToast("Please wait for all files to finish uploading", "error");
    return;
  }

  try {
    setLoading(true);
    
    const urls = uploadedFiles.map(f => f.url);
    
    const payload = {
      name: documentData.name,
      description: documentData.description,
      url: urls
    };

    const response = await uploadPendingAddDoc(payload, documentData._id);
    
    showToast("Documents submitted successfully!", "success");
    
    if (onSuccess) {
      await onSuccess();
    }
    
    onClose();
  } catch (error) {
    console.error("Error submitting documents:", error);
    console.error("Error details:", error.response?.data);
    showToast(error.response?.data?.message || "Failed to submit documents", "error");
  } finally {
    setLoading(false);
  }
};

  const handleViewDocument = (url) => {
    const viewUrl = `${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${url}`;
    window.open(viewUrl, "_blank");
  };

  const handleDownloadDocument = (url, docName) => {
    const downloadUrl = `${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/download/${url}`;
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = docName || "document";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  const isUploadMode = mode === "upload";
  const modalTitle = isUploadMode ? "Upload Documents" : "View Documents";

  return (
    <div className="view-document-modal-backdrop" onClick={onClose}>
      <div
        className="view-document-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="view-document-modal-header">
          <h2>{modalTitle}</h2>
          <button className="view-document-close-btn" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        <div className="view-document-modal-body">
          {isUploadMode ? (
            // Upload Mode UI
            <div className="upload-mode-container">
              <div className="upload-info-section">
                <h3>{documentData?.name || "Document Upload"}</h3>
                <p className="upload-description">
                  {documentData?.description || "Upload documents for this request"}
                </p>
              </div>

              <div className="upload-area">
                <label htmlFor="multi-file-input" className="upload-label">
                  <div className="upload-icon-area">
                    <BiUpload size={48} color="#3b82f6" />
                    <p>Click to select files or drag and drop</p>
                    <small>PDF, DOC, DOCX, TXT, PNG, JPEG, JPG, XLS, XLSX (Max 50MB each)</small>
                  </div>
                </label>
                <input
                  id="multi-file-input"
                  type="file"
                  multiple
                  accept={getAcceptString()}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>

              {/* Uploading Files Progress */}
              {uploadingFiles.length > 0 && (
                <div className="uploading-files-section">
                  <h4>Uploading...</h4>
                  {uploadingFiles.map(fileId => (
                    <div key={fileId} className="upload-progress-item">
                      <div className="progress-bar-container">
                        <div 
                          className="progress-bar-fill" 
                          style={{ width: `${uploadProgress[fileId] || 0}%` }}
                        />
                      </div>
                      <span className="progress-text">
                        {Math.round(uploadProgress[fileId] || 0)}%
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className="uploaded-files-section">
                  <h4>Uploaded Files ({uploadedFiles.length})</h4>
                  <div className="uploaded-files-list">
                    {uploadedFiles.map((file) => (
                      <div key={file.id} className="uploaded-file-item">
                        <div className="file-item-info">
                          <FiFileText size={20} color="#3b82f6" />
                          <span className="file-name">{file.name}</span>
                          {file.isExisting && (
                            <span className="existing-badge">Existing</span>
                          )}
                        </div>
                        <div className="file-item-actions">
                          <button
                            onClick={() => handleViewDocument(file.url)}
                            className="view-file-btn"
                            title="View file"
                          >
                            <FiFileText size={16} />
                          </button>
                          <button
                            onClick={() => handleRemoveFile(file.id)}
                            className="remove-file-btn"
                            title="Remove file"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {uploadedFiles.length === 0 && uploadingFiles.length === 0 && (
                <div className="no-files-message">
                  <p>No files uploaded yet. Click the area above to select files.</p>
                </div>
              )}
            </div>
          ) : (
            // View Mode UI (existing code)
            <>
              {loading ? (
                <div className="view-document-loading">
                  <div className="spinner"></div>
                  <p>Loading documents...</p>
                </div>
              ) : documents.length === 0 ? (
                <div className="view-document-empty">
                  <FiFileText size={48} />
                  <h3>No Documents Available</h3>
                  <p>
                    There are no uploaded documents for this court reference yet.
                  </p>
                </div>
              ) : (
                <div className="view-document-list">
                  {documents.map((doc) => (
                    <div key={doc._id} className="view-document-card">
                      <div className="view-document-card-header">
                        <h3>{doc.name}</h3>
                        <span className="view-document-status-badge">
                          {doc.status}
                        </span>
                      </div>

                      <div className="view-document-card-info">
                        <p>
                          <strong>Description:</strong>{" "}
                          {doc.description || "No description"}
                        </p>
                        <p>
                          <strong>Uploaded:</strong>{" "}
                          {new Date(doc.createdAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                        <p>
                          <strong>Total Files:</strong> {doc.url.length}
                        </p>
                      </div>

                      <div className="view-document-files">
                        {doc.url.map((fileUrl, index) => (
                          <div key={index} className="view-document-file-item">
                            <div className="view-document-file-info">
                              <div className="view-document-file-icon">
                                <FiFileText size={20} />
                              </div>
                              <div className="view-document-file-details">
                                <span className="view-document-file-name">
                                  {getFileNameFromUrl(fileUrl)}
                                </span>
                                <span className="view-document-file-type">
                                  {getFileExtension(fileUrl)}
                                </span>
                              </div>
                            </div>
                            <div className="view-document-file-actions">
                              <button
                                className="view-document-action-btn view-btn"
                                onClick={() => handleViewDocument(fileUrl)}
                                title="View Document"
                              >
                                <FiFileText size={16} />
                                View
                              </button>
                              <button
                                className="view-document-action-btn download-btn"
                                onClick={() =>
                                  handleDownloadDocument(
                                    fileUrl,
                                    getFileNameFromUrl(fileUrl)
                                  )
                                }
                                title="Download Document"
                              >
                                <FiDownload size={16} />
                                Download
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="view-document-modal-footer">
          {isUploadMode ? (
            <>
              <button 
                className="view-document-cancel-btn" 
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className="view-document-submit-btn" 
                onClick={handleSubmitDocuments}
                disabled={loading || uploadedFiles.length === 0 || uploadingFiles.length > 0}
              >
                {loading ? "Submitting..." : "Submit Documents"}
              </button>
            </>
          ) : (
            <button className="view-document-close-footer-btn" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
